import Product from '@grafikr/shopify-typescript/type/object/Product';
import Variant from '@grafikr/shopify-typescript/type/object/Variant';
import { component } from 'picoapp';
import formatMoney from 'utils/format-money';
import OptionSelector from 'utils/option-selector';
import { transitionEnd, repaint } from 'utils/node';
import { adding, added } from 'utils/add-to-cart-text';

interface ExtendedVariant extends Variant {
  inventory_quantity: number
  inventory_policy: string
}

interface CustomFormElement extends HTMLFormElement {
  _image_src: HTMLInputElement
}

export default component((node) => {
  const data: Product = JSON.parse(node.querySelector('[data-data]').textContent);

  const qtyInput: HTMLInputElement = node.querySelector('input[name="quantity"]');
  const qtyField: HTMLInputElement = node.querySelector('[data-qty]');
  const forms = [...node.querySelectorAll<HTMLFormElement>(`form[action="/cart/add"]#product_form_${data.id}`)];
  const stickyActionContainer: HTMLElement = node.querySelector('[data-sticky-actions]');
  const stickyActionsReference: HTMLButtonElement = node.querySelector('[data-sticky-action-reference]');
  const stickyAddToCart: HTMLButtonElement = node.querySelector('[data-sticky-add-to-cart]');
  const swatches = node.querySelectorAll<HTMLInputElement>('[data-swatch]');

  forms.forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const target = event.target as CustomFormElement;
      const { _image_src: imageSrc }: { _image_src: HTMLInputElement } = target;
      let featuredImage: string | null = null;

      if (stickyActionsReference) adding(stickyActionsReference);
      if (stickyAddToCart) adding(stickyAddToCart);

      await fetch(`${target.action}.js`, {
        method: target.method,
        body: new FormData(target),
      });

      if (stickyActionsReference) added(stickyActionsReference);
      if (stickyAddToCart) added(stickyAddToCart);

      if ((imageSrc instanceof HTMLInputElement)) {
        featuredImage = imageSrc.value;
      }

      const evt: CustomEvent<{ image: string | null; }> = new CustomEvent('cart:product:added', {
        detail: {
          image: featuredImage,
        },
      });

      document.dispatchEvent(evt);
    });
  });

  const toggleButtonStatus = (innerElement: HTMLSpanElement, disabled: boolean): void => {
    if (innerElement.parentElement instanceof HTMLButtonElement) {
      innerElement.parentElement.disabled = disabled;
    }
  };

  const updateAddToCartButtons = (
    elements: HTMLSpanElement[],
    text: string,
    disabled: boolean,
  ): void => {
    elements.forEach((element) => {
      element.textContent = text;
      toggleButtonStatus(element, disabled);
    });
  };

  const updateStatuses = (elements: HTMLElement[], activeStatus: 'available' | 'unavailable' | 'backorder') => {
    elements.forEach((element) => {
      element.hidden = element.dataset.productStatus !== activeStatus;
    });
  };

  const removeVAT = (price: number): number => price / 1.25;

  const getMaxQty = (variant: ExtendedVariant): string => {
    const {
      inventory_management: management,
      inventory_quantity: quantity,
      inventory_policy: policy,
    } = variant;

    if (management !== 'shopify' || policy === 'continue') {
      return '9999';
    }

    return quantity.toString();
  };

  const initOptions = () => {
    const DOMElements = {
      addToCarts: [...node.querySelectorAll<HTMLSpanElement>('[data-pdp-add-to-cart]')],
      prices: [...node.querySelectorAll<HTMLElement>('[data-price]')],
      compareAtPrices: [...node.querySelectorAll<HTMLElement>('[data-compare-at-price]')],
      priceExVATs: [...node.querySelectorAll<HTMLElement>('[data-price-ex]')],
      compareAtPriceExVATs: [...node.querySelectorAll<HTMLElement>('[data-compare-at-price-ex]')],
      statuses: [...node.querySelectorAll<HTMLElement>('[data-product-status]')],
      stickyActions: {
        variantTitle: node.querySelector('[data-sticky-actions] [data-update="variant-title"]'),
        variantInput: node.querySelector<HTMLInputElement>('[data-sticky-actions] [data-update="variant-input"]'),
        quantity: node.querySelector<HTMLInputElement>('[data-sticky-actions] [data-update="quantity"]'),
      },
      qtyField: qtyInput,
    };

    const callback = (variant: ExtendedVariant | null) => {
      if (variant) {
        DOMElements.prices.forEach((price) => {
          price.textContent = `${formatMoney(variant.price)}`;
        });

        DOMElements.qtyField.max = getMaxQty(variant);
        DOMElements.qtyField.value = '1';

        DOMElements.stickyActions.quantity.value = '1';
        DOMElements.stickyActions.variantInput.value = variant.id.toString();

        if (DOMElements.stickyActions.variantTitle) {
          DOMElements.stickyActions.variantTitle.textContent = variant.title;
        }

        if (variant.compare_at_price > variant.price) {
          DOMElements.compareAtPrices.forEach((compareAtPrice) => {
            compareAtPrice.hidden = false;
            compareAtPrice.innerHTML = `${formatMoney(variant.compare_at_price)}`;
          });
        } else {
          DOMElements.compareAtPrices.forEach((compareAtPrice) => {
            compareAtPrice.hidden = true;
          });
        }

        if (DOMElements.priceExVATs) {
          DOMElements.priceExVATs.forEach((priceExVAT) => {
            priceExVAT.textContent = `${formatMoney(removeVAT(variant.price))}`;
          });

          if (variant.compare_at_price > variant.price) {
            DOMElements.compareAtPriceExVATs.forEach((compareAtPriceExVAT) => {
              compareAtPriceExVAT.hidden = false;
              compareAtPriceExVAT.innerHTML = `${formatMoney(removeVAT(variant.compare_at_price))}`;
            });
          } else {
            DOMElements.compareAtPriceExVATs.forEach((compareAtPriceExVAT) => {
              compareAtPriceExVAT.hidden = true;
            });
          }
        }

        if (variant.available) {
          if (variant.inventory_quantity > 0) {
            updateAddToCartButtons(DOMElements.addToCarts, theme.strings.addToCart, false);
            updateStatuses(DOMElements.statuses, 'available');
          } else {
            updateAddToCartButtons(DOMElements.addToCarts, theme.strings.backorder, false);
            updateStatuses(DOMElements.statuses, 'backorder');
          }
        } else {
          updateAddToCartButtons(DOMElements.addToCarts, theme.strings.soldOut, true);
          updateStatuses(DOMElements.statuses, 'unavailable');
        }
      } else {
        updateAddToCartButtons(DOMElements.addToCarts, theme.strings.unavailable, true);
        updateStatuses(DOMElements.statuses, 'unavailable');
      }
    };

    const optionSelector = OptionSelector(`#product-${data.id}`, {
      data,
      onVariantSelected: callback,
      showSelectors: false,
    });

    swatches.forEach((swatch) => {
      const index = parseInt(swatch.dataset.swatch, 10) as 0 | 1 | 2;

      swatch.addEventListener('change', () => {
        optionSelector.setOption(index, swatch.value);
      });
    });
  };

  initOptions();

  const updateStickyActionsQty = (qty: string): void => {
    const stickyQtyField = node.querySelector('[data-sticky-actions] [data-update="quantity"]');

    if (!(stickyQtyField instanceof HTMLInputElement)) return;

    stickyQtyField.value = qty;
  };

  const subtractQty = (currQty: string) => Math.max(parseInt(currQty, 10) - 1, 1).toString();

  const addQty = (currQty: string, maxQty: string) => (
    Math.min(parseInt(currQty, 10) + 1, parseInt(maxQty, 10)).toString()
  );

  if (qtyInput && (qtyInput instanceof HTMLInputElement)) {
    const qtyUpdate = document.querySelectorAll<HTMLButtonElement>('[data-qty-update]');
    const qtyCount: HTMLElement = document.querySelector('[data-qty]');

    qtyUpdate.forEach((button) => {
      button.addEventListener('click', (e) => {
        const maxQty = qtyInput.max ?? '99';

        if (button.matches('[data-qty-update]')) {
          e.preventDefault();

          const { qtyUpdate: type } = button.dataset;
          let newQty: string;

          switch (type) {
            case 'add':
              // eslint-disable-next-line no-alert
              if (Number(qtyInput.value) >= Number(maxQty)) alert(`You can't add more than ${maxQty} items`);

              newQty = addQty(qtyInput.value, maxQty);
              qtyInput.value = newQty;
              updateStickyActionsQty(newQty);
              break;

            case 'sub':
              // eslint-disable-next-line no-alert
              if (Number(qtyInput.value) <= 1) alert('You can\'t choose less than 1 item');

              newQty = subtractQty(qtyInput.value);
              qtyInput.value = newQty;
              updateStickyActionsQty(newQty);
              break;

            default:
              break;
          }

          qtyCount.textContent = qtyInput.value;
          qtyField.textContent = qtyInput.value;
        }
      });
    });
  }

  const toggleStickyActions = (): void => {
    if (!stickyActionContainer || !stickyActionsReference) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          stickyActionContainer.hidden = false;
          repaint(stickyActionContainer);

          stickyActionContainer.classList.add('is-visible');
        } else {
          stickyActionContainer.classList.remove('is-visible');

          transitionEnd(stickyActionContainer, () => {
            if (!stickyActionContainer.classList.contains('is-visible')) {
              stickyActionContainer.hidden = true;
            }
          }, { once: true });
        }
      });
    }, { threshold: 1 });

    observer.observe(stickyActionsReference);
  };

  const closeStickyForm = node.querySelector('[data-close-sticky-form]');
  function closeFormManually() {
    stickyActionContainer.classList.remove('is-visible');
  }

  closeStickyForm.addEventListener('click', closeFormManually);

  toggleStickyActions();
});
